import React, { FC, useCallback, useMemo, useState } from "react";
import RGL, { Layouts, Responsive, WidthProvider } from "react-grid-layout";
import styled from "styled-components";
import { IGridLayout } from "@cayo/shared/api/types/schema";
import GridCell from "./GridCell";
import { ILayoutHierarchy } from "../Schemes/renderers/renderers.layout";
import { dataSetWidgetSignals } from "../Widgets/dataset-signals.hook";

const ResponsiveReactGridLayout = WidthProvider(Responsive);

export interface IResizableGrid extends IGridLayout {
  onDeleteCell?: (key: string) => void;
  onLayoutChange?: (layouts: Layouts) => Promise<void>;
  onBreakPointChange?: (newBreakpoint: string, newCols: number) => void;
  cells: ILayoutHierarchy[];
}

const DEFAULT_BP = "lg";

const Grid: FC<IResizableGrid> = (props) => {
  const {
    cells,
    onDeleteCell,
    onLayoutChange: onLayoutChangeProp,
    onBreakPointChange,
    sizes,
    items,
    ...rglProps
  } = props as any;

  const layouts = useMemo(() => (sizes || {}) as Layouts, [sizes, items]);

  const [currentBreakpoint, setCurrentBreakpoint] = useState(DEFAULT_BP);

  const onLayoutChange = useCallback(
    (newlayout: RGL.Layout[], nextLayouts: Layouts) => {
      if (onLayoutChangeProp) {
        onLayoutChangeProp(nextLayouts);
      }
    },
    [onLayoutChangeProp]
  );

  const onDeleteGridItem = useCallback(
    (itemKey: string) => {
      if (onDeleteCell) {
        onDeleteCell(itemKey);
      }
    },
    [onDeleteCell]
  );

  const generateDOM = useCallback(() => {
    const bp = currentBreakpoint || DEFAULT_BP;
    const bpLayouts = (layouts as any)[bp] || (layouts as any)[DEFAULT_BP] || [];

    return (cells || []).map((item: { name: string; render: () => JSX.Element }, idx: any) => {
      const layoutItem =
        bpLayouts.find((it: any) => it.i === item.name) ||
        ((layouts as any)[DEFAULT_BP] || []).find((it: any) => it.i === item.name);

      const width = layoutItem?.w ?? "";
      const height = layoutItem?.h ?? "";

      return (
        <div key={item.name!} data-grid={layoutItem}>
          <GridCell
            key={`${item.name}-${idx}-${width}-${height}`}
            name={item.name}
            render={item.render}
            onDelete={onDeleteGridItem}
          />
        </div>
      );
    });
  }, [cells, layouts, currentBreakpoint, onDeleteGridItem]);

  return (
    <StyledResponsiveReactGridLayout
      {...rglProps}
      layouts={layouts}
      isBounded={false}
      cols={{ lg: 12, md: 10, sm: 6, xs: 3, xxs: 1 }}
      onBreakpointChange={(newBreakpoint: string, newCols: number) => {
        setCurrentBreakpoint(newBreakpoint || DEFAULT_BP);
        if (onBreakPointChange) {
          onBreakPointChange(newBreakpoint, newCols);
        }
      }}
      onLayoutChange={onLayoutChange}
      autoSize={true}
      onResizeStop={() => {
        dataSetWidgetSignals.send("refresh");
        setTimeout(() => window.dispatchEvent(new Event("resize")), 10);
      }}
    >
      {generateDOM()}
    </StyledResponsiveReactGridLayout>
  );
};

const StyledResponsiveReactGridLayout = styled(ResponsiveReactGridLayout)`
  display: flex;
  flex-grow: 1;
  height: 100% !important;
  width: 100%;
`;

export default Grid;
